import type { Palette } from '../../_theme/palette.js';
import { DEFAULT_FONT_STACK, hexWithAlpha, shadeColor } from '../../_theme/palette.js';

export type FeedbackRatingStyle = 'stars' | 'thumbs' | 'nps' | 'emoji' | 'number' | false;

export interface FeedbackInline {
  name: string;
  type?: 'general' | 'feature' | 'other';
  title?: string;
  description?: string;
  rating?: FeedbackRatingStyle;
  comment?: boolean;
  attachments?: boolean;
  // 'full' / 'both' are aliases — both require name + email.
  identity?: 'none' | 'email' | 'full' | 'both';
  labels?: { submit?: string; thanks?: string; placeholder?: string };
  category?: string;
  metadata?: Record<string, unknown>;
  theme?: { primaryColor?: string; radius?: string; colorScheme?: 'light' | 'dark' | 'auto' };
}

export type WidgetStepKind = 'type-picker' | 'rating' | 'comment' | 'identity' | 'thanks';
export type WidgetRatingStyle = 'stars' | 'number-1-5' | 'emoji-5' | 'nps-0-10' | 'thumbs';

export interface WidgetStep {
  kind: WidgetStepKind;
  question?: string;
  ratingStyle?: WidgetRatingStyle;
  options?: { key: string; label: string; icon?: string }[];
  leftLabel?: string;
  rightLabel?: string;
  placeholder?: string;
  allowAttachments?: boolean;
  attachTypes?: string[];
  askName?: boolean;
  askEmail?: boolean;
  identityRequired?: 'none' | 'email' | 'both';
  required?: boolean;
  thanksTitle?: string;
  thanksBody?: string;
  thanksImageUrl?: string;
  thanksCtaLabel?: string;
  thanksCtaUrl?: string;
}

export interface FeedbackWidgetConfig {
  enabled?: boolean;
  layoutMode?: string;
  launcher?: { mode?: string; label?: string; icon?: string };
  steps?: WidgetStep[];
  theme?: {
    primaryColor?: string;
    cardBackground?: string;
    textColor?: string;
    buttonTextColor?: string;
    radius?: string;
    headerGradient?: boolean;
    colorScheme?: 'light' | 'dark';
  };
  labels?: { submitButton?: string; thanksTitle?: string; thanksBody?: string };
}

export interface Attachment {
  url: string;
  key?: string;
  kind: 'screenshot' | 'recording' | 'file';
  filename?: string;
}

export type CaptureBusyState = 'screenshot' | 'recording' | 'file' | null;

export interface FeedbackPalette extends Palette {
  useGradient: boolean;
  scheme: 'light' | 'dark';
}

export function resolveFeedbackPalette(theme?: FeedbackWidgetConfig['theme']): FeedbackPalette {
  const isDark = theme?.colorScheme === 'dark';
  const primary = theme?.primaryColor ?? '#e64980';
  const textMuted = isDark ? '#a1a1aa' : '#64748b';
  const text = theme?.textColor ?? (isDark ? '#f5f5f5' : '#0f172a');
  const card = theme?.cardBackground ?? (isDark ? '#1f1f23' : '#ffffff');
  const radius = theme?.radius === 'sharp' ? 4 : 10;
  const radiusLg = theme?.radius === 'sharp' ? 6 : 18;

  return {
    primary,
    primarySoft: hexWithAlpha(primary, isDark ? 0.22 : 0.12),
    primaryDeep: shadeColor(primary, -0.18),
    background: card,
    card,
    text,
    textMuted,
    onPrimary: theme?.buttonTextColor ?? '#ffffff',
    border: hexWithAlpha(textMuted, 0.2),
    radius,
    radiusLg,
    font: DEFAULT_FONT_STACK,
    useGradient: theme?.headerGradient ?? true,
    scheme: isDark ? 'dark' : 'light',
  };
}

export function normalizeRatingStyle(style?: WidgetRatingStyle | FeedbackRatingStyle): WidgetRatingStyle {
  if (!style) return 'stars';
  if (style === 'number') return 'number-1-5';
  if (style === 'nps') return 'nps-0-10';
  if (style === 'emoji') return 'emoji-5';
  if (style === 'stars') return 'stars';
  if (style === 'thumbs') return 'thumbs';
  return style;
}
